<template>
  <Header
    navbar-type="goBack"
    :screen-title="t('WITHDRAWAL')"
    @clicked="goBack"
    hide-help-btn-text
    show-help-btn
    :help-icon="useAsset('img/credit-line/help-question-mark.svg')"
  />
  <div class="input-withdrawal-amount px-3 mt-3">
    <label class="withdraw-label text-start px-3 align-self-start">
      {{ t("HOWMUCHWITHDRAW") }}
    </label>
    <div class="amount-input-wrapper">
      <WithdrawalAmountInput
        v-model="rawInput"
        :maxAmount="available"
        :autoFocus="true"
        ref="withdrawalInputRef"
      />
    </div>
    <div class="available-amount">
      {{
        t("CL_WITHDRAWAL.AVAILABLE", {
          availableCredit: currencyFormat(available, 0),
        })
      }}
    </div>
    <div class="mt-4 w-100">
      <Button
        v-if="amountInput && !errorMessage"
        class="mb-3"
        type="primary"
        fill
        :loading="withdrawalEstimateLoading"
        @click="estimateWithdrawal"
      >
        {{ t("CONTINUE") }}
      </Button>
      <div v-if="errorMessage" class="error-message text-center mx-auto">
        {{ errorMessage }}
      </div>
    </div>

    <DownUpModal
      v-if="showModal"
      btn-type="primary"
      modal-height="auto"
      :btnText="t('ACCEPT')"
      class-name="justify-content-start"
      :action-modal-loading="acceptWithdrawalLoading"
      @modalAction="acceptWithdrawal"
      @click-overlay="closeModal"
    >
      <template #custom-content>
        <ModalPaymentPlan
          :payment-term="paymentTerm"
          :amount="currencyFormat(withdrawalEstimate?.amount || 0)"
          :annual-interest-rate="annualInterestRate"
          :weekly-payment-amount="weeklyPaymentAmount"
          :next-payment-due-date="nextPaymentDueDate"
          :withdrawal-total-payment="withdrawalTotalPayment"
          :is-bank-transfer="selectedWithdrawMethod === WithdrawMethod.ONLINE"
          :bank-account-number="bankAccountNumber"
          :fee-schema="feeSchema"
          :withdrawal-estimate-fields="withdrawalEstimateFields"
        />
      </template>
    </DownUpModal>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRouter } from "vue-router";
import Button from "@/apps/credit-line/components/Button.vue";
import Header from "@/shared-components/consumer/Header.vue";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import DownUpModal from "@/apps/credit-line/components/DownUpModal.vue";
import ModalPaymentPlan from "@/apps/credit-line/components/Withdrawal/ModalPaymentPlan.vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { setLanguage } from "@/apps/credit-line/helpers/translations.helper";
import { safeAndroidCall } from "@/apps/credit-line/helpers/safe-android-call.helper";
import { useStore } from "vuex";
import { getErrorMessage } from "@/apps/credit-line/helpers/backCodesStrings.helper";
import WithdrawalAmountInput from "@/apps/credit-line/components/Withdrawal/WithdrawalAmountInput.vue";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { useSecureWithdrawal } from "@/apps/credit-line/hooks/useSecureWithdrawal";
import { IWithdrawalEstimate } from "@/apps/credit-line/models/IWithdrawalEstimate";
import { WithdrawMethod } from "@/apps/credit-line/types/withdrawMethod";
import { useWithdrawalEstimation } from "@/apps/credit-line/hooks/useWithdrawalEstimation";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import GoogleAnalyticsEventsHelper from "@/shared-helpers/google-analytics-events.helper";
import { AuthenticationHelper } from "../../helpers/authentication.helper";
import { KycUserFlow } from "../../enums/kycEnums";

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const rawInput = ref<string>("");
const errorMessage = ref<string>("");
const showModal = ref<boolean>(false);
const withdrawalEstimateLoading = ref<boolean>(false);
const acceptWithdrawalLoading = ref<boolean>(false);
const withdrawalInputRef = ref<InstanceType<
  typeof WithdrawalAmountInput
> | null>(null);

const amountInput = computed({
  get() {
    const num = Number(rawInput.value.replace(/[^0-9]/g, ""));
    return isNaN(num) ? 0 : num;
  },
  set(val: number | string) {
    rawInput.value = String(val);
  },
});

const {
  country,
  feeSchema,
  paymentTerm,
  needAmlSurvey,
  withdrawalEstimate,
  annualInterestRate,
  nextPaymentDueDate,
  weeklyPaymentAmount,
  withdrawalValidation,
  withdrawalTotalPayment,
  selectedWithdrawMethod,
  wasValidationSuccessful,
  withdrawalEstimateFields,
} = useWithdrawalEstimation();

const { summary } = useCreditLineSummary();

const { secureWithdrawal, alertMessage } = useSecureWithdrawal({
  bankTransfer: selectedWithdrawMethod.value === WithdrawMethod.ONLINE,
  t,
});

const focusInput = () => withdrawalInputRef.value?.focusInput();

const available = computed(() => Number(summary.value?.availableCredit ?? 0));
const minAmount = computed(() =>
  Number(summary.value?.minimumWithdrawalAmount ?? 0)
);
const bankAccountNumber = computed(
  () => summary.value?.bankAccountInfo?.recentValidatedClabe || 0
);

watch(rawInput, (val) => {
  if (val === "") {
    errorMessage.value = "";
    return;
  }

  const num = Number(val.replace(/[^0-9]/g, ""));
  errorMessage.value = validateAmount(num);
});

const validateAmount = (amount: number): string => {
  if (amount < minAmount.value) {
    DatadogLogger.Log(
      "Withdrawal amount below minimum",
      {
        deviceTag: safeAndroidCall("getDeviceTag"),
        enteredAmount: amount,
        minAmount: minAmount.value,
      },
      LogType.INFO
    );
    return t("CL_WITHDRAWAL.WITHDRAWAL_ENTER_AMOUNT_GREATER", {
      minAmount: currencyFormat(minAmount.value, 0),
    });
  }

  if (amount > available.value) {
    DatadogLogger.Log(
      "Withdrawal amount above maximum",
      {
        deviceTag: safeAndroidCall("getDeviceTag"),
        enteredAmount: amount,
        available: available.value,
      },
      LogType.INFO
    );
    return t("CL_WITHDRAWAL.WITHDRAWAL_AMOUNT_LESS_OR_EQUAL", {
      availableCredit: currencyFormat(available.value, 0),
    });
  }

  return "";
};

const closeModal = () => {
  acceptWithdrawalLoading.value = false;
  showModal.value = false;
  store.dispatch("creditLine/clearWithdrawalEstimate");
};

// TODO: Implement configurability for multi country support
const getValidationReference = (): KycUserFlow => {
  if (selectedWithdrawMethod.value === WithdrawMethod.ONLINE) {
    return KycUserFlow.creditLineOnlineWithdrawal;
  }
  return KycUserFlow.creditLineCashWithdrawal;
};

const estimateWithdrawal = async () => {
  withdrawalEstimateLoading.value = true;
  logAnalyticsEvent(
    GoogleAnalyticsEventsHelper.CreditLine.WithdrawAmountConfirmed,
    "Withdrawal"
  );

  try {
    await store.dispatch("creditLine/fetchWithdrawalEstimate", {
      amount: amountInput.value,
    });
    showModal.value = true;
  } catch (error) {
    errorMessage.value = getErrorMessage(
      error.response?.data?.code || "",
      t,
      safeAndroidCall("getDeviceTag") as string
    );
    focusInput();
  } finally {
    withdrawalEstimateLoading.value = false;
  }
};

const acceptWithdrawal = async () => {
  acceptWithdrawalLoading.value = true;

  const reference = getValidationReference();

  await store.dispatch("creditLine/fetchValidationRequired", {
    key: summary.value?.creditLineId,
    reference,
    deviceTag: AuthenticationHelper.getDeviceTag(),
  });

  // Handle cash withdrawals requiring validation
  if (
    reference === KycUserFlow.creditLineCashWithdrawal &&
    Array.isArray(withdrawalValidation.value?.requiredValidations) &&
    withdrawalValidation.value.requiredValidations.length > 0 &&
    !wasValidationSuccessful.value
  ) {
    return void router.push({
      name: "IdentityVerificationView",
      query: { hideCloseButton: "1", userFlow: reference },
    });
  }

  // Handle failed identity verification
  if (
    !withdrawalValidation.value?.wasSuccessful &&
    wasValidationSuccessful.value === false
  ) {
    return void router.push({
      name: "IdentityVerificationView",
      query: { hideCloseButton: "1", userFlow: reference },
    });
  }

  // Handle AML survey requirement
  if (needAmlSurvey.value) {
    return void router.push({
      name: "AmlSurveyView",
      query: {
        country: country.value,
      },
    });
  }

  // Process withdrawal when all validations pass
  secureWithdrawal({
    withdrawing: withdrawalEstimate.value?.amount as number,
    withdrawalEstimation: withdrawalEstimate.value as IWithdrawalEstimate,
    onSuccess: () => {
      acceptWithdrawalLoading.value = false;
      showModal.value = false;
      void store.dispatch("creditLine/clearWithdrawalEstimate");
      store.commit("creditLine/SET_SELECTED_WITHDRAW_METHOD", null);
      void router.push({ name: "CreditLineWithdrawalRequest" });
    },
    onError: () => {
      acceptWithdrawalLoading.value = false;
      showModal.value = false;
      errorMessage.value = alertMessage.value;
    },
  });
};

const handleInitialValidationError = async () => {
  if (withdrawalValidation.value && !withdrawalValidation.value.wasSuccessful) {
    void router.replace({
      name: "IdentityVerificationView",
      query: { hideCloseButton: "1" },
    });
    return;
  }
};

const goBack = () => {
  safeAndroidCall("showNav");
  router.back();
};

onBeforeMount(() => {
  setLanguage(getCurrentInstance()?.proxy);
});

onMounted(() => {
  safeAndroidCall("hideNav");
  handleInitialValidationError();
  focusInput();
});
</script>

<style lang="scss" scoped>
.input-withdrawal-amount {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}
.withdraw-label {
  color: var(--Typography-Text-1, #111);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 29px;
  padding-top: 15px;
  padding-bottom: 15px;
}
.amount-input-wrapper {
  display: flex;
  align-items: center;
  font-size: 2.5rem;
  margin-top: 15px;
  margin-bottom: 1rem;
}
.amount-input {
  color: var(--Typography-Text-2-ICON, #595a5a);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 40px;
  font-style: normal;
  font-weight: 400;
  line-height: 29px;
  border: none;
  text-align: center;
  outline: none;
}
.available-amount {
  margin-top: 15px;
  color: var(--Typography-Text-2-ICON, #595a5a);
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px;
}
.error-message {
  color: var(--Alerts-Error-Error, #c9372c);
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  width: 232px;
  margin-top: 12px;
}
</style>
